import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Search, ShoppingBag } from 'lucide-react';
import { Badge } from '@/shared/ui/badge';
import { Input } from '@/shared/ui/input';
import { Pagination } from '@/shared/ui/pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { EmptyState } from '@/shared/components/empty-state';
import { brl, decimal } from '@/shared/lib/format';
import type { CampaignReport, CampaignReportDispatch } from '../api/use-campaign-report';

const BUYERS_PAGE_SIZE = 15;

function dateTime(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

const DISPATCH_STATUS: Record<
  CampaignReportDispatch['status'],
  { label: string; variant: 'success' | 'warning' | 'destructive' }
> = {
  success: { label: 'Concluído', variant: 'success' },
  running: { label: 'Em andamento', variant: 'warning' },
  error: { label: 'Com erro', variant: 'destructive' },
};

export function DispatchesTable({ dispatches }: { dispatches: CampaignReport['dispatches'] }) {
  if (dispatches.length === 0) {
    return <p className="px-5 py-8 text-center text-sm text-muted-foreground">Nenhum disparo no período.</p>;
  }
  // Mais recente primeiro; o back devolve em ordem cronológica para os gráficos.
  const rows = [...dispatches].reverse().slice(0, 50);
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Disparo</TableHead>
          <TableHead>Situação</TableHead>
          <TableHead className="text-right">Envios</TableHead>
          <TableHead className="text-right">Entregues</TableHead>
          <TableHead className="text-right">Falhas</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((d) => {
          const status = DISPATCH_STATUS[d.status] ?? DISPATCH_STATUS.success;
          return (
            <TableRow key={d.id}>
              <TableCell className="whitespace-nowrap">{dateTime(d.date)}</TableCell>
              <TableCell>
                <Badge variant={status.variant} title={d.error ?? undefined}>
                  {status.label}
                </Badge>
              </TableCell>
              <TableCell className="text-right tabular-nums">{decimal(d.sent)}</TableCell>
              <TableCell className="text-right tabular-nums">{decimal(d.delivered)}</TableCell>
              <TableCell className="text-right tabular-nums">{decimal(d.failed)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

interface BuyersTableProps {
  buyers: CampaignReport['buyers'];
  truncated: boolean;
  /** Loja da tela — fallback quando a compra não traz o passbook do cliente. */
  idPassbook: string;
}

/** Quem comprou dentro da janela, com link para o lead (abre em Leads → detalhe). */
export function BuyersTable({ buyers, truncated, idPassbook }: BuyersTableProps) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? buyers.filter((b) => b.name.toLowerCase().includes(q)) : buyers;
  }, [buyers, search]);
  const pageRows = filtered.slice(page * BUYERS_PAGE_SIZE, (page + 1) * BUYERS_PAGE_SIZE);

  if (buyers.length === 0) {
    return (
      <EmptyState
        icon={ShoppingBag}
        title="Nenhuma compra atribuída"
        description="Ninguém que recebeu esta campanha comprou dentro da janela de performance, ainda."
        className="py-10"
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative max-w-xs">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          placeholder="Buscar cliente…"
          className="pl-9"
        />
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Cliente</TableHead>
            <TableHead>Recebeu em</TableHead>
            <TableHead>1ª compra</TableHead>
            <TableHead className="text-right">Pedidos</TableHead>
            <TableHead className="text-right">Valor</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {pageRows.map((b) => {
            const to = `/loja/${b.idPassbook || idPassbook}/leads/${b.userId}`;
            return (
              <TableRow key={b.userId}>
                <TableCell className="font-medium">
                  <Link to={to} className="hover:underline">
                    {b.name || 'Sem nome'}
                  </Link>
                </TableCell>
                <TableCell className="whitespace-nowrap">{dateTime(b.receivedAt)}</TableCell>
                <TableCell className="whitespace-nowrap">{dateTime(b.firstPurchaseAt)}</TableCell>
                <TableCell className="text-right tabular-nums">{decimal(b.orders)}</TableCell>
                <TableCell className="text-right tabular-nums">{b.revenue > 0 ? brl(b.revenue) : '—'}</TableCell>
                <TableCell>
                  <Link
                    to={to}
                    aria-label={`Abrir ${b.name || 'cliente'} em Leads`}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <ExternalLink className="size-4" />
                  </Link>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {filtered.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">Nenhum cliente com esse nome.</p>
      ) : (
        <Pagination page={page} pageSize={BUYERS_PAGE_SIZE} total={filtered.length} onPageChange={setPage} />
      )}
      {truncated ? (
        <p className="text-xs text-muted-foreground">
          Mostrando os {decimal(buyers.length)} clientes de maior valor. O total acima considera todos.
        </p>
      ) : null}
    </div>
  );
}
